import React, { useEffect, useRef, useState } from "react";
import { useBundle } from "@amzn/react-arb-tools";
import { getFormattedText, getText } from "./utils/pantherHelper";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import {
  Input,
  InputFooter,
  Select,
} from "@amzn/stencil-react-components/form";
import { Label } from "@amzn/stencil-react-components/text";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Card } from "@amzn/stencil-react-components/card";
import { H1 } from "@amzn/stencil-react-components/text";
import { Spinner } from "@amzn/stencil-react-components/spinner";
import { SpinnerSize } from "@amzn/stencil-react-components/dist/submodules/spinner/interfaces";
import { Link, BrowserRouter } from "react-router-dom";
import defaultTheme from "./theme/defaultTheme";
import { Feature } from "./config";
import { sendMetrics } from "./reporting/adobeMetrics";
import {
  processPhoneNumber,
  formatAsYouType,
  COUNTRY_PHONE_PREFIXES,
  validatePhoneNumberRealTime,
} from "./utils/phoneNumberUtils";
import { getIAHPrivacyPolicyLink } from "./domainOrgMapper";

export const InputCard = ({
  initChatApp,
  inputEditorDir,
  feature,
  language,
  country,
  business,
  channel,
  ingressPoint,
  agencyName,
  settings,
  locale,
}) => {
  const [bundle, isBundleLoading] = useBundle("strings");
  const [emailAddress, setEmailAddress] = useState("");
  const [isEmailAddressValid, setIsEmailAddressValid] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(true);
  const inputRef = useRef(null);

  useEffect(() => {
    const emitAdobeEvent = async () => {
      await sendMetrics({
        country: country,
        language: language,
        business: business,
        channel: channel,
        ingressPoint: ingressPoint,
        event: "chat home",
      });
    };

    emitAdobeEvent();
  }, [country, language, business, channel, ingressPoint]);

  const loadingComponent = (
    <Col height={"100vh"} alignItems={"center"} justifyContent={"center"}>
      <Spinner size={SpinnerSize.Medium} showText={false} />
    </Col>
  );

  const isStandardChat = feature === Feature.STANDARD_CHAT;
  const isGrievanceReportingEmail =
    feature === Feature.GRIEVANCE_REPORTING_EMAIL;
  const isGrievanceReportingPhone =
    feature === Feature.GRIEVANCE_REPORTING_PHONE;
  const isGrievanceReporting =
    isGrievanceReportingEmail || isGrievanceReportingPhone;

  if (isBundleLoading) {
    return loadingComponent;
  }

  const submitContactInfo = () => {
    if (isGrievanceReportingPhone) {
      const { isValid, e164Number } = processPhoneNumber(
        phoneNumber,
        country,
        true
      );

      if (!phoneNumber || !isValid) {
        setIsPhoneNumberValid(false);
        inputRef.current?.focus();
      } else {
        initChatApp(e164Number);
      }
    } else {
      // Email validation
      if (!emailAddress || !validateEmail(emailAddress)) {
        setIsEmailAddressValid(false);
        inputRef.current?.focus();
      } else {
        initChatApp(emailAddress);
      }
    }
  };

  const TermsAndConditionsContent = ({
    bundle,
    isGrievanceReporting,
    settings,
  }) => {
    const baseUrls = {
      hiringProcessUrl: (
        <Col style={{ display: "inline" }} key={"hiring-process"}>
          <BrowserRouter>
            <Link
              to={{
                pathname: "https://hiring.amazon.com/hiring-process#/",
              }}
              target="_blank"
            >
              {getText(bundle, "hiring-process")}
            </Link>
          </BrowserRouter>
        </Col>
      ),
      applicationGuideUrl: (
        <Col style={{ display: "inline" }} key={"application-guide"}>
          <BrowserRouter>
            <Link
              to={{
                pathname:
                  "https://hiring.amazon.com/hiring-process/application-guide#/",
              }}
              target="_blank"
            >
              {getText(bundle, "application-guide")}
            </Link>
          </BrowserRouter>
        </Col>
      ),
    };

    if (!isGrievanceReporting) {
      return getFormattedText(bundle, "terms-and-conditions-message", baseUrls);
    }

    let policyLink;
    const privacyNotice = getText(bundle, "privacy-notice");

    if (settings) {
      if (settings.stage && isGrievanceReportingPhone) {
        policyLink = getIAHPrivacyPolicyLink(settings, agencyName, locale);
      } else if (!isGrievanceReportingPhone) {
        policyLink = "https://www.amazon.jobs/en/privacy_page#/";
      }
    }

    return getFormattedText(
      bundle,
      "terms-and-conditions-message-with-privacy-notice",
      {
        ...baseUrls,
        privacyNoticeUrl: (
          <Col style={{ display: "inline" }} key={"privacy-notice"}>
            {policyLink ? (
              <BrowserRouter>
                <Link
                  to={{
                    pathname: policyLink,
                  }}
                  target="_blank"
                >
                  {privacyNotice}
                </Link>
              </BrowserRouter>
            ) : (
              privacyNotice
            )}
          </Col>
        ),
      }
    );
  };

  return (
    <Col alignItems={"center"} padding={"3rem 1rem 3rem 1rem"} gridGap={"2rem"}>
      <H1 dataTestId={"input-card-header"}>
        {getText(
          bundle,
          isStandardChat ? "input-card-header" : "input-card-header-grievance"
        )}
      </H1>
      <Card
        justifyContent="center"
        alignItems="center"
        maxWidth={800}
        width={"100%"}
        backgroundColor={defaultTheme.palette.whisper}
      >
        <Col
          gridGap={"3rem"}
          width={"100%"}
          height={"100%"}
          alignItems={"center"}
        >
          <Col width={"100%"} gridGap={"1rem"}>
            {isGrievanceReportingPhone ? (
              // Phone number input
              <>
                <Row gridGap="8px" alignItems="flex-end">
                  <Col gridGap="S200" minWidth="120px" flexShrink={0}>
                    <Label
                      htmlFor="country-code-select"
                      id="country-code-select-label"
                    >
                      {getText(bundle, "input-card-country-code")}
                    </Label>
                    <Select
                      id="country-code-select"
                      labelId="country-code-select-label"
                      options={Object.keys(COUNTRY_PHONE_PREFIXES).map(
                        (countryName) =>
                          `${COUNTRY_PHONE_PREFIXES[countryName].prefix}`
                      )}
                      value={`${COUNTRY_PHONE_PREFIXES[country]?.prefix}`}
                    />
                  </Col>
                  <Col gridGap="S200" flex="1" minWidth={0}>
                    <Label
                      htmlFor="phone-number-input"
                      dataTestId={"phone-number-label"}
                      style={{ overflow: "hidden" }}
                    >
                      {getText(
                        bundle,
                        "input-card-mobile-number-of-candidate"
                      ).replace(/\s+\S+$/, "")}{" "}
                      <span style={{ whiteSpace: "nowrap" }}>
                        {
                          getText(
                            bundle,
                            "input-card-mobile-number-of-candidate"
                          ).match(/\S+$/)?.[0]
                        }{" "}
                        <strong style={{ color: "red" }} aria-hidden="true">
                          *
                        </strong>
                      </span>
                    </Label>
                    <Input
                      dataTestId={"phone-number-input"}
                      aria-required="true"
                      dir={inputEditorDir}
                      id="phone-number-input"
                      ref={inputRef}
                      type="tel"
                      aria-invalid={!isPhoneNumberValid ? "true" : "false"}
                      aria-describedby="phone-number-error"
                      maxLength={20}
                      onChange={(e) => {
                        // Store the raw input value
                        const rawValue = e.target.value.replace(/\D/g, "");
                        setPhoneNumber(rawValue);
                        // Validate the phone number
                        const isValid = validatePhoneNumberRealTime(
                          rawValue,
                          country
                        );
                        setIsPhoneNumberValid(isValid);
                      }}
                      value={formatAsYouType(phoneNumber, country)}
                      error={!isPhoneNumberValid}
                    />
                  </Col>
                </Row>
                {!isPhoneNumberValid ? (
                  <InputFooter
                    dataTestId={"phone-number-error"}
                    id="phone-number-error"
                    error
                  >
                    {getText(bundle, "input-card-mobile-error-message")}
                  </InputFooter>
                ) : null}
              </>
            ) : (
              // Email input for other countries
              <>
                <Row>
                  <Label
                    htmlFor="email-address-input"
                    dataTestId={"email-address-label"}
                  >
                    {getText(
                      bundle,
                      isStandardChat
                        ? "input-card-email-address"
                        : "input-card-email-address-of-candidate"
                    )}{" "}
                    <strong style={{ color: "red" }} aria-hidden="true">
                      *
                    </strong>{" "}
                  </Label>
                </Row>

                <Input
                  dataTestId={"email-address-input"}
                  aria-required="true"
                  dir={inputEditorDir}
                  id="email-address-input"
                  ref={inputRef}
                  type="text"
                  placeholder="example@email.com"
                  aria-invalid={!isEmailAddressValid ? "true" : "false"}
                  aria-describedby="email-address-error"
                  onChange={(e) => {
                    setIsEmailAddressValid(true);
                    setEmailAddress(e.target.value);
                  }}
                  error={!isEmailAddressValid}
                />
                {!isEmailAddressValid ? (
                  <InputFooter
                    dataTestId={"email-address-error"}
                    id="email-address-error"
                    error
                  >
                    {getText(bundle, "input-card-error-message")}
                  </InputFooter>
                ) : null}
              </>
            )}
          </Col>
          <Col width={250}>
            <Button
              dataTestId={"submit-button"}
              variant={ButtonVariant.Primary}
              onClick={submitContactInfo}
            >
              {getText(
                bundle,
                isStandardChat
                  ? "input-card-chat-now-button"
                  : "input-card-start-grievance-report-button"
              )}
            </Button>
          </Col>
        </Col>
      </Card>
      <Col
        justifyContent="center"
        alignItems="center"
        maxWidth={800}
        width={"100%"}
      >
        <Label
          fontSize={"0.75em"}
          display={"inline"}
          data-testid="terms-and-conditions"
        >
          <TermsAndConditionsContent
            bundle={bundle}
            isGrievanceReporting={isGrievanceReporting}
            settings={settings}
          />
        </Label>
      </Col>
    </Col>
  );
};

export const validateEmail = (emailAddress) => {
  return String(emailAddress)
    .toLowerCase()
    .match(/^([a-zA-Z0-9_\-.+]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,5})$/); //matching the regex similar to HBA registration page, this is to fix issue with accented characters mentioned in https://issues.amazon.com/issues/T-STARSHIP-1334
};
